import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  MenuItem,
  Autocomplete
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';
import { Save, ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import AppLayout from '../../components/layout/AppLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import FormErrorSummary from '../../components/common/FormErrorSummary';
import scadenzeService from '../../services/scadenzeService';
import clientiService from '../../services/clientiService';
import tipologieService from '../../services/tipologieService';
import usersService from '../../services/usersService';
import type { CreateScadenzaDto, UpdateScadenzaDto } from '../../types/scadenza.types';
import type { Cliente } from '../../types/cliente.types';
import { getErrorMessage } from '../../utils/api';

const ScadenzaFormPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!id;

  const [clientiSearchQuery, setClientiSearchQuery] = useState('');

  const { control, handleSubmit, reset, formState: { errors }, watch } = useForm<CreateScadenzaDto | UpdateScadenzaDto>({
    mode: 'onBlur',
    defaultValues: {
      clienteId: '',
      tipologiaId: '',
      dataScadenza: '',
      descrizione: '',
      assegnatoAId: ''
    }
  });

  const watchedTipologiaId = watch('tipologiaId');

  // Fetch scadenza if editing
  const { data: scadenza, isLoading: scadenzaLoading } = useQuery({
    queryKey: ['scadenza', id],
    queryFn: () => scadenzeService.getById(id!),
    enabled: isEdit
  });

  // Fetch clienti - carica inizialmente i primi 10, poi filtra in base alla ricerca
  const { data: clienti } = useQuery({
    queryKey: ['clienti-search', clientiSearchQuery],
    queryFn: () => clientiService.search(clientiSearchQuery),
    staleTime: 30 * 1000, // 30 secondi - per vedere subito nuovi clienti
  });

  // Stabilizza le funzioni per l'Autocomplete del cliente
  const getOptionLabel = useCallback((option: Cliente) =>
    `${option.cognome} ${option.nome} (${option.codiceFiscale})`.trim(), []);

  const isOptionEqualToValue = useCallback((option: Cliente, value: Cliente | null) =>
    option.id === value?.id, []);

  // Fetch tipologie
  const { data: tipologie } = useQuery({
    queryKey: ['tipologie-active'],
    queryFn: () => tipologieService.getAllActive(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch operatori
  const { data: operatori } = useQuery({
    queryKey: ['operatori'],
    queryFn: () => usersService.getOperatori(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: (data: CreateScadenzaDto | UpdateScadenzaDto) =>
      isEdit
        ? scadenzeService.update(id!, data as UpdateScadenzaDto)
        : scadenzeService.create(data as CreateScadenzaDto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scadenze'] });
      queryClient.invalidateQueries({ queryKey: ['scadenze-stats'] });
      queryClient.invalidateQueries({ queryKey: ['scadenze-mese-corrente'] });
      enqueueSnackbar(
        isEdit ? 'Scadenza aggiornata con successo' : 'Scadenza creata con successo',
        { variant: 'success' }
      );
      navigate('/scadenze');
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante il salvataggio'), { variant: 'error' });
    }
  });

  useEffect(() => {
    if (scadenza) {
      reset({
        clienteId: scadenza.clienteId,
        tipologiaId: scadenza.tipologiaId,
        dataScadenza: scadenza.dataScadenza,
        descrizione: scadenza.descrizione || '',
        assegnatoAId: scadenza.assegnatoAId || ''
      });
    }
  }, [scadenza]);

  const onSubmit = (data: CreateScadenzaDto | UpdateScadenzaDto) => {
    saveMutation.mutate(data);
  };

  if (isEdit && scadenzaLoading) {
    return (
      <AppLayout>
        <LoadingSpinner message="Caricamento scadenza..." />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/scadenze')}
            variant="outlined"
          >
            Indietro
          </Button>
          <Typography variant="h4" component="h1" fontWeight={600}>
            {isEdit ? 'Modifica Scadenza' : 'Nuova Scadenza'}
          </Typography>
        </Box>

        <Paper sx={{ p: 3 }}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            aria-label={isEdit ? 'Modifica scadenza' : 'Crea nuova scadenza'}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormErrorSummary errors={errors} />

              <Controller
                name="clienteId"
                control={control}
                rules={{ required: 'Cliente obbligatorio' }}
                render={({ field, fieldState }) => {
                  const selectedCliente = clienti?.find((c) => c.id === field.value) || null;

                  return (
                    <Autocomplete
                      options={clienti || []}
                      getOptionLabel={getOptionLabel}
                      value={selectedCliente}
                      onChange={(_, newValue) => {
                        field.onChange(newValue?.id || '');
                      }}
                      onInputChange={(_, newInputValue, reason) => {
                        // Aggiorna la query di ricerca quando l'utente digita
                        if (reason === 'input') {
                          setClientiSearchQuery(newInputValue);
                        }
                      }}
                      isOptionEqualToValue={isOptionEqualToValue}
                      filterOptions={(x) => x} // Disabilita filtro locale, usa solo risultati dal server
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Cliente"
                          required
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  );
                }}
              />

              <Controller
                name="tipologiaId"
                control={control}
                rules={{ required: 'Tipologia obbligatoria' }}
                render={({ field: { value, onChange, onBlur, name }, fieldState }) => (
                  <TextField
                    select
                    label="Tipologia"
                    required
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    value={value || ''}
                    onChange={onChange}
                    onBlur={onBlur}
                    name={name}
                  >
                    {(tipologie || []).map((tip) => (
                      <MenuItem key={tip.id} value={tip.id}>
                        {tip.nome}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
                <Controller
                  name="dataScadenza"
                  control={control}
                  rules={{ required: 'Data scadenza obbligatoria' }}
                  render={({ field: { value, onChange }, fieldState }) => {
                    // Converti il valore string in Date object per il DatePicker
                    const dateValue = value ? new Date(value) : null;

                    // In dev mode, usa DateTimePicker per testare i reminder a 1 minuto
                    if (import.meta.env.DEV) {
                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <DateTimePicker
                            label="Data e Ora Scadenza (DEV)"
                            value={dateValue}
                            onChange={(newValue) => {
                              if (newValue) {
                                onChange(newValue.toISOString());
                              } else {
                                onChange('');
                              }
                            }}
                            slotProps={{
                              textField: {
                                required: true,
                                error: !!fieldState.error,
                                helperText: fieldState.error?.message,
                                fullWidth: true
                              }
                            }}
                            format="dd/MM/yyyy HH:mm"
                            ampm={false}
                          />
                          <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            onClick={() => {
                              // Imposta la data a NOW + 2 minuti per testare il reminder a 1 minuto
                              const nowPlus2Min = new Date(Date.now() + 2 * 60 * 1000);
                              onChange(nowPlus2Min.toISOString());
                            }}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            ⏱️ TEST: Imposta NOW + 2 minuti
                          </Button>
                        </Box>
                      );
                    }

                    return (
                      <DatePicker
                        label="Data Scadenza"
                        value={dateValue}
                        onChange={(newValue) => {
                          if (newValue) {
                            // Crea una data UTC con lo stesso giorno/mese/anno
                            // per evitare problemi di timezone
                            const year = newValue.getFullYear();
                            const month = newValue.getMonth();
                            const day = newValue.getDate();
                            // Usa mezzogiorno UTC per evitare shift di giorno
                            const utcDate = new Date(Date.UTC(year, month, day, 12, 0, 0, 0));
                            onChange(utcDate.toISOString());
                          } else {
                            onChange('');
                          }
                        }}
                        slotProps={{
                          textField: {
                            required: true,
                            error: !!fieldState.error,
                            helperText: fieldState.error?.message,
                            fullWidth: true
                          }
                        }}
                        format="dd/MM/yyyy"
                      />
                    );
                  }}
                />
              </LocalizationProvider>

              {/* Mostra i giorni di preavviso dalla tipologia selezionata */}
              {watchedTipologiaId && (
                <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Giorni Preavviso (dalla tipologia)
                  </Typography>
                  <Typography variant="body2">
                    {(() => {
                      const selectedTipologia = tipologie?.find(t => t.id === watchedTipologiaId);
                      const intervalli = selectedTipologia?.intervalliReminder || [selectedTipologia?.giorniPreavvisoDefault || 30];
                      return intervalli.map(g => g === 0.0007 ? '1 minuto (TEST)' : `${g} giorni`).join(', ');
                    })()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Per modificare i giorni di preavviso, modifica la tipologia
                  </Typography>
                  {(() => {
                    const selectedTipologia = tipologie?.find(t => t.id === watchedTipologiaId);
                    const hasTestReminder = selectedTipologia?.intervalliReminder?.includes(0.0007);
                    return hasTestReminder ? (
                      <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 'bold', display: 'block', mt: 1 }}>
                        ⚠️ Reminder TEST attivo: notifica inviata 1 minuto prima della scadenza
                      </Typography>
                    ) : null;
                  })()}
                </Box>
              )}

              <Controller
                name="assegnatoAId"
                control={control}
                render={({ field: { value, onChange, onBlur, name, ref } }) => (
                  <TextField
                    select
                    label="Assegna a"
                    value={value || ''}
                    onChange={onChange}
                    onBlur={onBlur}
                    name={name}
                  >
                    <MenuItem value="">Nessuno</MenuItem>
                    {(operatori || []).map((op) => (
                      <MenuItem key={op.id} value={op.id}>
                        {op.nome} {op.cognome}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <Controller
                name="descrizione"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Descrizione"
                    multiline
                    rows={4}
                    placeholder="Inserisci una descrizione..."
                  />
                )}
              />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/scadenze')}
                  disabled={saveMutation.isPending}
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={saveMutation.isPending}
                >
                  {isEdit ? 'Aggiorna' : 'Crea'} Scadenza
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </Box>
    </AppLayout>
  );
};

export default ScadenzaFormPage;
